import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Toaster, toast } from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./AdminReservations.css";
import PerformanceDashboard from "./PerformanceDashboard";

const API_BASE = "https://djatervoyage.com/backend_djatervoyage/gestionnaire";
const PAGE_SIZE = 15;

const STATUS_LABELS = {
  en_attente: "En attente",
  en_cours: "En cours",
  confirmée: "Confirmée",
  terminée: "Terminée",
};

const STATUS_COLORS = {
  en_attente: "bg-yellow-100 text-yellow-800 border-yellow-300",
  en_cours: "bg-blue-100 text-blue-800 border-blue-300",
  confirmée: "bg-green-100 text-green-800 border-green-300",
  terminée: "bg-gray-100 text-gray-800 border-gray-300",
};

const PAYMENT_STATUS_COLORS = {
  success: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  failed: "bg-red-100 text-red-800",
};












// Logo de ton agence
const LOGO_URL = "https://djatervoyage.com/logo.png";

const printReservation = async (reservation) => {
  try {
    // Utiliser flight_data et passenger_data directement (plus fiable)
    const flightData = Array.isArray(reservation.flight_data) 
      ? reservation.flight_data 
      : typeof reservation.flight_data === "string"
      ? JSON.parse(reservation.flight_data)
      : [];
    
    const passengerData = Array.isArray(reservation.passenger_data)
      ? reservation.passenger_data
      : typeof reservation.passenger_data === "string"
      ? JSON.parse(reservation.passenger_data)
      : [];

    if (!flightData.length || !passengerData.length) {
      alert("❌ Données de vol ou passagers manquantes.");
      return;
    }

    const doc = new jsPDF("p", "mm", "a4");

    // === Chargement du logo ===
    const logo = await fetch(LOGO_URL)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
      )
      .catch(() => null);

    // === Entête moderne avec fond coloré ===
    doc.setFillColor(16, 185, 129); // Vert moderne
    doc.rect(0, 0, 210, 45, 'F');
    
    if (logo) {
      doc.addImage(logo, "PNG", 15, 8, 25, 25);
    }
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(255, 255, 255);
    doc.text("CONFIRMATION DE RÉSERVATION", 105, 20, { align: "center" });
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Djater Voyage - Votre partenaire de confiance", 105, 30, { align: "center" });

    // === Informations Générales ===
    let y = 55;
    doc.setFillColor(249, 250, 251);
    doc.roundedRect(15, y - 5, 180, 42, 3, 3, 'F');
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("📋 Informations de la Réservation", 20, y + 3);
    y += 10;

    const info = [
      ["Référence", reservation.reference],
      ["Code PNR", reservation.pnr_code || "En attente de confirmation"],
      ["Email Client", reservation.customer_email],
      ["Téléphone", reservation.customer_phone || "-"],
      ["Montant Payé", `${parseFloat(reservation.payment_amount).toLocaleString()} ${reservation.payment_currency}`],
      ["Date de Réservation", new Date(reservation.created_at).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })],
    ];

    autoTable(doc, {
      startY: y,
      head: [["Champ", "Valeur"]],
      body: info,
      theme: "plain",
      styles: { 
        fontSize: 10, 
        cellPadding: 3,
        lineColor: [229, 231, 235],
        lineWidth: 0.1
      },
      headStyles: { 
        fillColor: [59, 130, 246], 
        textColor: 255,
        fontStyle: 'bold',
        halign: 'left'
      },
      columnStyles: { 
        0: { fontStyle: "bold", cellWidth: 50, textColor: [55, 65, 81] },
        1: { cellWidth: 130, textColor: [107, 114, 128] }
      },
      alternateRowStyles: { fillColor: [249, 250, 251] }
    });
    y = doc.lastAutoTable.finalY + 12;

    // === Passagers avec design moderne ===
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("👥 Liste des Passagers", 15, y);
    y += 6;

    // Récupérer les numéros de billets
    let ticketNumbers = [];
    try {
      ticketNumbers = reservation.ticket_numbers 
        ? (typeof reservation.ticket_numbers === 'string' 
            ? JSON.parse(reservation.ticket_numbers) 
            : reservation.ticket_numbers)
        : [];
    } catch (e) {
      console.error('Erreur parsing ticket_numbers:', e);
    }

    const travelers = passengerData.map((p, i) => {
      const travelerType = p.travelerType === 'ADULT' ? 'Adulte' : (p.travelerType === 'CHILD' ? 'Enfant' : 'Bébé');
      const ticketNumber = ticketNumbers.find(t => t.passenger_index === i)?.ticket_number || "-";
      return [
        i + 1,
        `${p.name?.firstName || ""} ${p.name?.lastName || ""}`,
        travelerType,
        p.gender || "-",
        p.dateOfBirth || "-",
        p.documents?.[0]?.documentType || "PASSPORT",
        p.documents?.[0]?.number || "-",
        ticketNumber,
        p.documents?.[0]?.nationality || "-",
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [["#", "Nom Complet", "Type", "Sexe", "Naissance", "Doc", "N° Document", "N° Billet", "Nationalité"]],
      body: travelers,
      theme: "striped",
      headStyles: { 
        fillColor: [59, 130, 246], 
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 8
      },
      styles: { 
        fontSize: 8, 
        cellPadding: 2,
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 35, halign: 'left', fontStyle: 'bold' },
        2: { cellWidth: 15 },
        3: { cellWidth: 12 },
        4: { cellWidth: 22 },
        5: { cellWidth: 18 },
        6: { cellWidth: 25 },
        7: { cellWidth: 28, fontStyle: 'bold', textColor: [22, 163, 74] },
        8: { cellWidth: 17 }
      },
      alternateRowStyles: { fillColor: [249, 250, 251] }
    });
    y = doc.lastAutoTable.finalY + 12;

    // === Détails des vols avec design moderne ===
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("✈️ Détails des Vols", 15, y);
    y += 6;

    const flightOffer = flightData[0];
    if (flightOffer) {
      flightOffer.itineraries?.forEach((it, i) => {
        // Vérifier si on doit créer une nouvelle page
        if (y > 240) {
          doc.addPage();
          y = 20;
        }

        // En-tête de l'itinéraire avec fond coloré
        doc.setFillColor(59, 130, 246);
        doc.roundedRect(15, y - 5, 180, 10, 2, 2, 'F');
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(`Itinéraire ${i + 1} — Durée totale: ${it.duration}`, 20, y + 1);
        y += 8;

        const segments = it.segments.map((s, idx) => {
          const depDate = new Date(s.departure.at);
          const arrDate = new Date(s.arrival.at);
          const baggageQty = s.fareDetailsBySegment?.[0]?.includedCheckedBags?.quantity || 0;
          
          return [
            idx + 1,
            `${s.departure.iataCode}\n${depDate.toLocaleDateString('fr-FR')} ${depDate.toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}`,
            `${s.arrival.iataCode}\n${arrDate.toLocaleDateString('fr-FR')} ${arrDate.toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}`,
            `${s.carrierCode} ${s.number}`,
            s.aircraft?.code || "-",
            s.duration || "-",
            s.cabin || "Economy",
            `${baggageQty} pc(s)`
          ];
        });

        autoTable(doc, {
          startY: y,
          head: [["#", "Départ", "Arrivée", "Vol", "Avion", "Durée", "Classe", "Bagages"]],
          body: segments,
          theme: "striped",
          styles: { 
            fontSize: 8, 
            cellPadding: 2.5,
            halign: 'center',
            valign: 'middle'
          },
          headStyles: { 
            fillColor: [37, 99, 235], 
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 35, fontSize: 7 },
            2: { cellWidth: 35, fontSize: 7 },
            3: { cellWidth: 25, fontStyle: 'bold' },
            4: { cellWidth: 18 },
            5: { cellWidth: 20 },
            6: { cellWidth: 25 },
            7: { cellWidth: 20 }
          },
          alternateRowStyles: { fillColor: [249, 250, 251] },
          didDrawCell: (data) => {
            // Ajouter des icônes visuelles
            if (data.section === 'body' && data.column.index === 3) {
              doc.setTextColor(37, 99, 235);
            }
          }
        });
        y = doc.lastAutoTable.finalY + 8;
      });

      // === Informations tarifaires avec design encadré ===
      if (flightOffer.price) {
        if (y > 250) {
          doc.addPage();
          y = 20;
        }

        const { total, base, currency, fees } = flightOffer.price;
        const taxes = fees?.reduce((sum, fee) => sum + parseFloat(fee.amount || 0), 0) || 0;

        doc.setFillColor(16, 185, 129);
        doc.roundedRect(15, y - 5, 180, 30, 3, 3, 'F');
        
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text("💰 Détails Tarifaires", 20, y + 3);
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(`Tarif de base: ${parseFloat(base).toLocaleString()} ${currency}`, 20, y + 11);
        doc.text(`Taxes et frais: ${taxes.toLocaleString()} ${currency}`, 20, y + 18);
        
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(`TOTAL: ${parseFloat(total).toLocaleString()} ${currency}`, 120, y + 15);
        
        y += 35;

        // Compagnie validatrice
        if (flightOffer.validatingAirlineCodes?.length) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(107, 114, 128);
          doc.text(
            `✈️ Compagnie validatrice: ${flightOffer.validatingAirlineCodes.join(", ")}`,
            15,
            y
          );
          y += 8;
        }
      }
    }

    // === Section Informations Importantes ===
    if (y > 230) {
      doc.addPage();
      y = 20;
    }

    doc.setFillColor(254, 243, 199);
    doc.setDrawColor(245, 158, 11);
    doc.setLineWidth(0.5);
    doc.roundedRect(15, y, 180, 40, 3, 3, 'FD');
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(146, 64, 14);
    doc.text("⚠️ INFORMATIONS IMPORTANTES", 20, y + 8);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 53, 15);
    const importantInfo = [
      "• Présentez-vous à l'aéroport 3 heures avant le départ pour les vols internationaux",
      "• Munissez-vous de votre passeport et du code PNR",
      "• Vérifiez les exigences visa de votre pays de destination",
      "• Confirmez les restrictions de bagages auprès de la compagnie aérienne"
    ];
    
    importantInfo.forEach((info, idx) => {
      doc.text(info, 20, y + 16 + (idx * 6));
    });
    
    y += 48;

    // === Pied de page moderne avec fond ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Fond du footer
      doc.setFillColor(31, 41, 55);
      doc.rect(0, 277, 210, 20, 'F');
      
      // Logo dans le footer (petit)
      if (logo) {
        doc.addImage(logo, "PNG", 15, 280, 12, 12);
      }
      
      // Texte du footer
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text("Djater Voyage", 30, 285);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(209, 213, 219);
      doc.text("📞 +221 XX XXX XX XX  |  📧 assistance@djatervoyage.com  |  🌐 www.djatervoyage.com", 30, 290);
      
      // Numéro de page
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(156, 163, 175);
      doc.text(`Page ${i} / ${pageCount}`, 185, 287, { align: "right" });
      
      // Date de génération
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`,
        105,
        294,
        { align: "center" }
      );
    }

    // === Sauvegarde ===
    doc.save(`confirmation_${reservation.reference}.pdf`);
    toast.success("✅ PDF de confirmation généré avec succès !");
  } catch (e) {
    console.error("Erreur impression :", e);
    toast.error("❌ Impossible de générer le PDF.");
  }
};











const AdminReservationsApp = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmingPayment, setConfirmingPayment] = useState(null);
  
  // 🔐 Helper pour vérifier les permissions
  const hasPermission = useCallback((permission) => {
    if (!admin) return false;
    
    const permissions = {
      admin: ['view_reservations', 'view_stats', 'view_profits', 'view_performance', 'export_data', 'confirm_amadeus', 'mark_paid', 'manage_status', 'manage_pnr', 'manage_tickets', 'send_email', 'print', 'delete'],
      assistant: ['view_reservations', 'manage_status', 'manage_pnr', 'manage_tickets', 'send_email', 'print']
    };
    
    return permissions[admin.role]?.includes(permission) || false;
  }, [admin]);
  
  // États réservations
  const [reservations, setReservations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [dateFilter, setDateFilter] = useState({ start: "", end: "" });
  const [amountFilter, setAmountFilter] = useState({ min: "", max: "" });
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewMode, setViewMode] = useState("table"); // table, cards, analytics, performance
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showPNRModal, setShowPNRModal] = useState(null); // {id, currentPNR}
  const [pnrInput, setPnrInput] = useState("");
  const [periodFilter, setPeriodFilter] = useState("30days"); // today, 7days, 30days, thisMonth, year, custom
  const [showTicketsModal, setShowTicketsModal] = useState(null); // {id, passengers, currentTickets}
  const [ticketInputs, setTicketInputs] = useState([]); // [{passenger_index, ticket_number}]
  const [showDeleteModal, setShowDeleteModal] = useState(null); // {id, reference}

  // Vérifie si une session admin existe déjà
  useEffect(() => {
    fetch(`${API_BASE}/check_session.php`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data.loggedIn) {
          setAdmin(data.admin);
          loadReservations();
        }
      })
      .finally(() => setLoading(false));
  }, []);



function cleanFlightOffers(flightOffers) {
  return flightOffers.map(offer => {
    // On extrait uniquement les champs valides attendus par Amadeus
    const {
      type,
      id,
      source,
      instantTicketingRequired,
      nonHomogeneous,
      oneWay,
      lastTicketingDate,
      lastTicketingDateTime,
      numberOfBookableSeats,
      itineraries,
      price,
      pricingOptions,
      validatingAirlineCodes,
      travelerPricings
    } = offer;

    return {
      type,
      id,
      source,
      instantTicketingRequired,
      nonHomogeneous,
      oneWay,
      lastTicketingDate,
      lastTicketingDateTime,
      numberOfBookableSeats,
      itineraries,
      price,
      pricingOptions,
      validatingAirlineCodes,
      travelerPricings
    };
  });
}






  const confirmWithAmadeus = async (reservation) => {
    try {
      // 🔹 S'assurer que les champs sont bien parsés
      const flightOffers = typeof reservation.flight_data === "string"
        ? JSON.parse(reservation.flight_data)
        : reservation.flight_data;

      const travelers = typeof reservation.passenger_data === "string"
        ? JSON.parse(reservation.passenger_data)
        : reservation.passenger_data;

      console.log("➡️ Données envoyées pour Amadeus :", { reference: reservation.reference, flightOffers, travelers });

      const response = await fetch(`${API_BASE}/confirmation_vol.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          data: {
            reference: reservation.reference,
            flightOffers: cleanFlightOffers(flightOffers),
            travelers
          }
        }),
      });

      const result = await response.json();
      console.log("✅ Réponse Amadeus :", result);

      if (result.success) {
        toast.success("✅ Réservation confirmée sur Amadeus !");
      } else {
        toast.error(result.message || "❌ Échec confirmation Amadeus");
      }
    } catch (error) {
      console.error("⚠️ Erreur confirmWithAmadeus:", error);
      toast.error("❌ Erreur de connexion au serveur");
    }
  };



  // 🔹 Mettre à jour le paiement
  const markAsPaid = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api.php`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          id,
          payment_status: "success"
        }),
      });
      const data = await res.json();
      if (data.success) {
        setReservations((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, payment_status: "success" } : r
          )
        );
        toast.success("✅ Paiement marqué comme effectué !");
      } else {
        toast.error(data.message || "Erreur lors de la mise à jour du paiement");
      }
    } catch {
      toast.error("❌ Impossible de mettre à jour le paiement");
    }
  };





  // 🔹 Charger les réservations
  const loadReservations = () => {
    fetch(`${API_BASE}/api.php`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setReservations(data.data);
        else toast.error(data.message || "Erreur lors du chargement");
      })
      .catch(() => toast.error("Impossible de charger les réservations"));
  };

  // 🔹 Connexion admin
  const login = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const res = await fetch(`${API_BASE}/login_admin.php`, {
      method: "POST",
      body: form,
      credentials: "include",
    });
    const data = await res.json();
    if (data.success) {
      setAdmin(data.admin);
      toast.success("Connexion réussie ✅");
      loadReservations();
    } else {
      toast.error(data.message || "Échec de la connexion");
    }
  };

  // 🔹 Déconnexion
  const logout = async () => {
    await fetch(`${API_BASE}/logout_admin.php`, {
      method: "POST",
      credentials: "include",
    });
    setAdmin(null);
    setReservations([]);
    toast.success("Déconnecté");
  };

  // 🔹 Gestion des détails
  const openDetails = (reservation) => {
    try {
      reservation.passenger_data = JSON.parse(reservation.passenger_data);
      reservation.flight_data = JSON.parse(reservation.flight_data);
    } catch (e) {
      console.error("Erreur parsing JSON :", e);
    }
    setSelected(reservation);
  };
  const closeDetails = () => setSelected(null);

  // 🔹 Suivi
  const openTracking = (reservation) => setTracking(reservation);
  const closeTracking = () => setTracking(null);

  const steps = ["en_attente", "en_cours", "confirmée", "terminée"];

  // 🔹 Mise à jour statut
  const updateStatus = useCallback(async (id, newStatus) => {
    try {
      const res = await fetch(`${API_BASE}/api.php`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, reservation_status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setReservations((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, reservation_status: newStatus } : r
          )
        );
        if (tracking && tracking.id === id) {
          setTracking({ ...tracking, reservation_status: newStatus });
        }
        toast.success("✅ Statut mis à jour !");
      } else {
        toast.error(data.message || "Erreur lors de la mise à jour");
      }
    } catch {
      toast.error("❌ Impossible de mettre à jour le statut");
    }
  }, [tracking]);

  // 🔹 Mise à jour du code PNR
  const updatePNR = useCallback(async (id, pnrCode) => {
    try {
      const res = await fetch(`${API_BASE}/api.php`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, pnr_code: pnrCode }),
      });
      const data = await res.json();
      if (data.success) {
        setReservations((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, pnr_code: pnrCode } : r
          )
        );
        if (selected && selected.id === id) {
          setSelected({ ...selected, pnr_code: pnrCode });
        }
        toast.success("✅ Code PNR enregistré !");
      } else {
        toast.error(data.message || "Erreur lors de la mise à jour du PNR");
      }
    } catch {
      toast.error("❌ Impossible de mettre à jour le PNR");
    }
  }, [selected]);

  // 🔹 Mise à jour des numéros de billets
  const updateTicketNumbers = useCallback(async (id, ticketNumbers) => {
    try {
      const res = await fetch(`${API_BASE}/api.php`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, ticket_numbers: ticketNumbers }),
      });
      const data = await res.json();
      if (data.success) {
        setReservations((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, ticket_numbers: JSON.stringify(ticketNumbers) } : r
          )
        );
        if (selected && selected.id === id) {
          setSelected({ ...selected, ticket_numbers: JSON.stringify(ticketNumbers) });
        }
        toast.success("✅ Numéros de billets enregistrés !");
      } else {
        toast.error(data.message || "Erreur lors de la mise à jour des numéros de billets");
      }
    } catch {
      toast.error("❌ Impossible de mettre à jour les numéros de billets");
    }
  }, [selected]);

  // 🔹 Envoyer la confirmation par email au client
  const sendConfirmationEmail = useCallback(async (reservationId) => {
    const toastId = toast.loading("📧 Envoi de l'email en cours...");
    try {
      const res = await fetch(`${API_BASE}/send_confirmation.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reservation_id: reservationId }),
      });
      const data = await res.json();
      
      toast.dismiss(toastId);
      
      if (data.success) {
        toast.success(`✅ Email envoyé avec succès à ${data.email}`);
      } else {
        toast.error(data.message || "Erreur lors de l'envoi de l'email");
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("❌ Impossible d'envoyer l'email");
      console.error("Erreur envoi email:", error);
    }
  }, []);

  // 🔹 Supprimer une réservation
  const deleteReservation = useCallback(async (id) => {
    const toastId = toast.loading("🗑️ Suppression en cours...");
    try {
      const res = await fetch(`${API_BASE}/api.php`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      
      toast.dismiss(toastId);
      
      if (data.success) {
        setReservations((prev) => prev.filter((r) => r.id !== id));
        setShowDeleteModal(null);
        toast.success("✅ Réservation supprimée avec succès");
      } else {
        toast.error(data.message || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("❌ Impossible de supprimer la réservation");
      console.error("Erreur suppression:", error);
    }
  }, []);

  // 🔹 Calculer le bénéfice d'une réservation
  const calculateProfit = useCallback((reservation) => {
    try {
      const clientAmount = parseFloat(reservation.payment_amount) || 0;
      
      // Parser flight_data si c'est une chaîne
      let flightData = reservation.flight_data;
      if (typeof flightData === 'string') {
        flightData = JSON.parse(flightData);
      }
      
      // Récupérer le prix total de la compagnie (premier offre)
      const airlinePrice = flightData?.[0]?.price?.total 
        ? parseFloat(flightData[0].price.total) 
        : 0;
      
      return clientAmount - airlinePrice;
    } catch (error) {
      console.error("Erreur calcul bénéfice:", error);
      return 0;
    }
  }, []);

  // 🔹 Filtrage et recherche avancés
  const filteredReservations = useMemo(() => {
    let filtered = reservations;
    
    // Recherche textuelle
    if (search) {
      filtered = filtered.filter(
        (r) =>
          r.reference?.toLowerCase().includes(search.toLowerCase()) ||
          r.customer_email?.toLowerCase().includes(search.toLowerCase()) ||
          r.customer_phone?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    // Filtre statut réservation
    if (statusFilter) {
      filtered = filtered.filter((r) => r.reservation_status === statusFilter);
    }
    
    // Filtre statut paiement
    if (paymentFilter) {
      filtered = filtered.filter((r) => r.payment_status === paymentFilter);
    }
    
    // Filtre par date
    if (dateFilter.start) {
      filtered = filtered.filter((r) => new Date(r.created_at) >= new Date(dateFilter.start));
    }
    if (dateFilter.end) {
      filtered = filtered.filter((r) => new Date(r.created_at) <= new Date(dateFilter.end));
    }
    
    // Filtre par montant
    if (amountFilter.min) {
      filtered = filtered.filter((r) => parseFloat(r.payment_amount) >= parseFloat(amountFilter.min));
    }
    if (amountFilter.max) {
      filtered = filtered.filter((r) => parseFloat(r.payment_amount) <= parseFloat(amountFilter.max));
    }
    
    // Tri
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === "payment_amount") {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortBy === "created_at") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtered;
  }, [reservations, search, statusFilter, paymentFilter, dateFilter, amountFilter, sortBy, sortOrder]);

  // 🔹 Statistiques calculées (basées sur les réservations filtrées)
  const stats = useMemo(() => {
    const total = filteredReservations.length;
    const confirmed = filteredReservations.filter(r => r.reservation_status === "confirmée").length;
    const pending = filteredReservations.filter(r => r.reservation_status === "en_attente").length;
    const inProgress = filteredReservations.filter(r => r.reservation_status === "en_cours").length;
    const completed = filteredReservations.filter(r => r.reservation_status === "terminée").length;
    
    const totalRevenue = filteredReservations
      .filter(r => r.payment_status === "success")
      .reduce((sum, r) => sum + (parseFloat(r.payment_amount) || 0), 0);
    
    const pendingPayments = filteredReservations
      .filter(r => r.payment_status !== "success")
      .reduce((sum, r) => sum + (parseFloat(r.payment_amount) || 0), 0);
    
    const successfulPayments = filteredReservations.filter(r => r.payment_status === "success").length;
    const conversionRate = total > 0 ? ((successfulPayments / total) * 100).toFixed(1) : 0;
    
    // 💰 Calculs comptables
    let totalCost = 0;
    let totalProfit = 0;
    
    filteredReservations.forEach(r => {
      if (r.payment_status === "success") {
        try {
          const clientAmount = parseFloat(r.payment_amount) || 0;
          let flightData = r.flight_data;
          if (typeof flightData === 'string') {
            flightData = JSON.parse(flightData);
          }
          const airlinePrice = flightData?.[0]?.price?.total 
            ? parseFloat(flightData[0].price.total) 
            : 0;
          
          totalCost += airlinePrice;
          totalProfit += (clientAmount - airlinePrice);
        } catch (error) {
          console.error("Erreur calcul stats:", error);
        }
      }
    });
    
    const profitMargin = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : 0;
    
    return {
      total,
      confirmed,
      pending,
      inProgress,
      completed,
      totalRevenue,
      pendingPayments,
      conversionRate,
      successfulPayments,
      totalCost,
      totalProfit,
      profitMargin
    };
  }, [filteredReservations]);

  // 🔹 Pagination (défini avant pour éviter "use-before-define")
  const totalPages = Math.ceil(filteredReservations.length / PAGE_SIZE);
  const paginatedReservations = filteredReservations.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // 🔹 Fonctions d'export
  const exportToCSV = useCallback(() => {
    const headers = ["Référence", "Email", "Téléphone", "Montant", "Devise", "Statut Paiement", "Statut Réservation", "Date"];
    const rows = filteredReservations.map(r => [
      r.reference,
      r.customer_email,
      r.customer_phone,
      r.payment_amount,
      r.payment_currency,
      r.payment_status,
      r.reservation_status,
      r.created_at
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `reservations_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("✅ Export CSV réussi !");
  }, [filteredReservations]);

  const exportToPDF = useCallback(() => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Rapport des Réservations", 14, 22);
    doc.setFontSize(11);
    doc.text(`Généré le ${new Date().toLocaleDateString()}`, 14, 30);
    
    autoTable(doc, {
      startY: 35,
      head: [["Référence", "Email", "Montant", "Statut", "Date"]],
      body: filteredReservations.map(r => [
        r.reference,
        r.customer_email,
        `${r.payment_amount} ${r.payment_currency}`,
        STATUS_LABELS[r.reservation_status],
        new Date(r.created_at).toLocaleDateString()
      ]),
      theme: "striped",
      headStyles: { fillColor: [37, 99, 235] }
    });
    
    doc.save(`rapport_reservations_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("✅ Export PDF réussi !");
  }, [filteredReservations]);

  // 🔹 Actions bulk
  const handleBulkStatusChange = useCallback(async (newStatus) => {
    if (selectedRows.length === 0) {
      toast.error("Aucune réservation sélectionnée");
      return;
    }
    
    try {
      await Promise.all(
        selectedRows.map(id => updateStatus(id, newStatus))
      );
      setSelectedRows([]);
      toast.success(`✅ ${selectedRows.length} réservation(s) mise(s) à jour`);
    } catch (error) {
      toast.error("❌ Erreur lors de la mise à jour groupée");
    }
  }, [selectedRows, updateStatus]);

  const handleSelectAll = useCallback(() => {
    if (selectedRows.length === paginatedReservations.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(paginatedReservations.map(r => r.id));
    }
  }, [selectedRows, paginatedReservations]);

  // 🔹 Réinitialiser filtres
  const resetFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("");
    setPaymentFilter("");
    setDateFilter({ start: "", end: "" });
    setAmountFilter({ min: "", max: "" });
    setPage(1);
    toast.success("Filtres réinitialisés");
  }, []);

  // 🔹 Composant Pagination
  const Pagination = () => (
    <div className="flex justify-between items-center mt-6 px-4">
      <div className="text-sm text-gray-600">
        Affichage de {(page - 1) * PAGE_SIZE + 1} à {Math.min(page * PAGE_SIZE, filteredReservations.length)} sur {filteredReservations.length} résultat(s)
      </div>
      <div className="flex items-center gap-2">
        <button
          className="px-4 py-2 rounded bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={page === 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          ◀ Précédent
        </button>
        <div className="flex gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (page <= 3) {
              pageNum = i + 1;
            } else if (page >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = page - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                className={`px-3 py-2 rounded ${
                  page === pageNum
                    ? "bg-blue-600 text-white"
                    : "bg-white border border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => setPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        <button
          className="px-4 py-2 rounded bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={page === totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Suivant ▶
        </button>
      </div>
    </div>
  );

  // ⏳ Loading initial
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Chargement...</p>
      </div>
    );
  }

  // 🔒 Si pas connecté → Formulaire login
  if (!admin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
        <Toaster />
        
        {/* Arrière-plan animé */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>

        {/* Carte de connexion */}
        <div className="relative z-10 bg-white rounded-3xl shadow-2xl overflow-hidden max-w-md w-full mx-4">
          {/* En-tête avec gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-10 text-center relative">
            {/* Logo */}
            <div className="mb-4">
              <img 
                src="https://djatervoyage.com/logo.png" 
                alt="Djater Voyage" 
                className="w-24 h-24 mx-auto object-contain drop-shadow-lg"
              />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2 drop-shadow-md">
              Djater Voyage
            </h1>
            <p className="text-blue-100 text-sm font-medium">
              Panneau d'Administration
            </p>
            
            {/* Décoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>
          </div>

          {/* Formulaire */}
          <form onSubmit={login} className="px-8 py-10">
            

            {/* Champ utilisateur */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nom d'utilisateur
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  type="text"
                  name="username"
                  placeholder="Entrez votre nom d'utilisateur"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all"
                  required
                />
              </div>
            </div>

            {/* Champ mot de passe */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="password"
                  name="password"
                  placeholder="Entrez votre mot de passe"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all"
                  required
                />
              </div>
            </div>

            {/* Bouton de connexion */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Se connecter
            </button>

            {/* Message de sécurité */}
            <div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Accès sécurisé :</strong> Cette page est réservée aux administrateurs et assistants autorisés de Djater Voyage.
                </p>
              </div>
            </div>
          </form>

          {/* Pied de page */}
          <div className="px-8 py-4 bg-gray-50 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} Djater Voyage. Tous droits réservés.
            </p>
          </div>
        </div>

        {/* CSS pour l'animation blob */}
        <style>{`
          @keyframes blob {
            0%, 100% { transform: translate(0, 0) scale(1); }
            25% { transform: translate(20px, -50px) scale(1.1); }
            50% { transform: translate(-20px, 20px) scale(0.9); }
            75% { transform: translate(50px, 50px) scale(1.05); }
          }
          .animate-blob {
            animation: blob 7s infinite;
          }
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          .animation-delay-4000 {
            animation-delay: 4s;
          }
        `}</style>
      </div>
    );
  }

  // ✅ Vue admin
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Toaster position="top-right" />
      
      {/* Header moderne */}
      <header className="bg-white shadow-md border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img 
                src="https://djatervoyage.com/logo.png" 
                alt="Djater Voyage" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Gestion des Réservations
                </h1>
                <p className="text-sm text-gray-500">
                  Bienvenue, <span className="font-semibold">{admin?.name || admin?.username}</span>
                  {admin?.role && (
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                      admin.role === 'admin' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {admin.role === 'admin' ? '👑 Administrateur' : '👤 Assistant'}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {hasPermission('view_performance') && (
                <button
                  onClick={() => setViewMode(viewMode === "table" ? "performance" : "table")}
                  className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition font-medium"
                >
                  {viewMode === "table" ? "📊 Performances" : "📋 Tableau"}
                </button>
              )}
              <button
                onClick={logout}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-md transition font-medium"
              >
                🚪 Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        
        {/* Dashboard Statistiques */}
        {hasPermission('view_stats') ? (
          // Vue complète pour Admin
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Total Réservations</p>
                    <h3 className="text-3xl font-bold text-gray-800 mt-1">{stats.total}</h3>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📋</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Revenus Clients</p>
                    <h3 className="text-3xl font-bold text-gray-800 mt-1">
                      {stats.totalRevenue.toLocaleString()} XOF
                    </h3>
                    <p className="text-xs text-green-600 mt-1">
                      {stats.successfulPayments} paiements validés
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">💰</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-orange-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Coûts Compagnies</p>
                    <h3 className="text-3xl font-bold text-gray-800 mt-1">
                      {stats.totalCost.toLocaleString()} XOF
                    </h3>
                    <p className="text-xs text-orange-600 mt-1">
                      À payer aux compagnies
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">✈️</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-emerald-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Bénéfices Nets</p>
                    <h3 className="text-3xl font-bold text-emerald-600 mt-1">
                      {stats.totalProfit.toLocaleString()} XOF
                    </h3>
                    <p className="text-xs text-emerald-600 mt-1">
                      Marge: {stats.profitMargin}%
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">💎</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Ligne supplémentaire pour En Attente et Taux de Conversion */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-yellow-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">En Attente</p>
                    <h3 className="text-3xl font-bold text-gray-800 mt-1">{stats.pending}</h3>
                    <p className="text-xs text-yellow-600 mt-1">
                      {stats.pendingPayments.toLocaleString()} XOF à confirmer
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">⏳</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Taux de Conversion</p>
                    <h3 className="text-3xl font-bold text-gray-800 mt-1">{stats.conversionRate}%</h3>
                    <p className="text-xs text-purple-600 mt-1">
                      {stats.confirmed} confirmées
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📈</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          // Vue simplifiée pour Assistant
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Réservations</p>
                  <h3 className="text-3xl font-bold text-gray-800 mt-1">{stats.total}</h3>
                  <p className="text-xs text-gray-500 mt-1">Toutes les réservations</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📋</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Confirmées</p>
                  <h3 className="text-3xl font-bold text-gray-800 mt-1">{stats.confirmed}</h3>
                  <p className="text-xs text-green-600 mt-1">
                    Réservations confirmées
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">✅</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-yellow-500">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm font-medium">En Attente</p>
                  <h3 className="text-3xl font-bold text-gray-800 mt-1">{stats.pending}</h3>
                  <p className="text-xs text-yellow-600 mt-1">
                    À traiter
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">⏳</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vue Performances avec Graphiques (Admin uniquement) */}
        {viewMode === "performance" && hasPermission('view_performance') && (
          <div className="mb-6">
            {/* Sélecteur de période */}
            <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-xl font-bold text-gray-800">📈 Bilan de Performances</h2>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setPeriodFilter("today")}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      periodFilter === "today"
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Aujourd'hui
                  </button>
                  <button
                    onClick={() => setPeriodFilter("7days")}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      periodFilter === "7days"
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    7 jours
                  </button>
                  <button
                    onClick={() => setPeriodFilter("30days")}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      periodFilter === "30days"
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    30 jours
                  </button>
                  <button
                    onClick={() => setPeriodFilter("thisMonth")}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      periodFilter === "thisMonth"
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Mois actuel
                  </button>
                  <button
                    onClick={() => setPeriodFilter("year")}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      periodFilter === "year"
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Année {new Date().getFullYear()}
                  </button>
                </div>
              </div>
            </div>

            {/* Dashboard avec graphiques */}
            <PerformanceDashboard reservations={reservations} period={periodFilter} />
          </div>
        )}

        {/* Vue Analytics */}
        {viewMode === "analytics" && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">� Vue d'ensemble</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Répartition par Statut</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <span className="font-medium">En attente</span>
                    <span className="text-lg font-bold text-yellow-700">{stats.pending}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="font-medium">En cours</span>
                    <span className="text-lg font-bold text-blue-700">{stats.inProgress}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="font-medium">Confirmées</span>
                    <span className="text-lg font-bold text-green-700">{stats.confirmed}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">Terminées</span>
                    <span className="text-lg font-bold text-gray-700">{stats.completed}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Actions Rapides</h3>
                <div className="space-y-2">
                  <button
                    onClick={exportToCSV}
                    className="w-full p-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition font-medium shadow-md"
                  >
                    📥 Exporter en CSV
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="w-full p-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition font-medium shadow-md"
                  >
                    📄 Exporter en PDF
                  </button>
                  <button
                    onClick={loadReservations}
                    className="w-full p-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition font-medium shadow-md"
                  >
                    🔄 Actualiser les données
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filtres avancés */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-800">🔍 Recherche et Filtres</h2>
            <div className="flex gap-2">
              <button
                onClick={resetFilters}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition font-medium"
              >
                ↺ Réinitialiser
              </button>
              {hasPermission('export_data') && (
                <button
                  onClick={() => setShowExportModal(true)}
                  className="px-3 py-1.5 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition font-medium"
                >
                  📤 Exporter
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="🔍 Rechercher..."
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Tous les statuts</option>
              {steps.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>

            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={paymentFilter}
              onChange={(e) => {
                setPaymentFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Tous les paiements</option>
              <option value="success">Payé</option>
              <option value="pending">En attente</option>
              <option value="failed">Échoué</option>
            </select>

            <div className="flex gap-2">
              <select
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="created_at">Trier par date</option>
                <option value="payment_amount">Trier par montant</option>
                <option value="reference">Trier par référence</option>
              </select>
              <button
                onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
                title={sortOrder === "asc" ? "Croissant" : "Décroissant"}
              >
                {sortOrder === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="flex gap-2 items-center">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Date :</label>
              <input
                type="date"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={dateFilter.start}
                onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
              />
              <span className="text-gray-500">→</span>
              <input
                type="date"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={dateFilter.end}
                onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
              />
            </div>

            <div className="flex gap-2 items-center">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Montant (XOF) :</label>
              <input
                type="number"
                placeholder="Min"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={amountFilter.min}
                onChange={(e) => setAmountFilter({ ...amountFilter, min: e.target.value })}
              />
              <span className="text-gray-500">→</span>
              <input
                type="number"
                placeholder="Max"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={amountFilter.max}
                onChange={(e) => setAmountFilter({ ...amountFilter, max: e.target.value })}
              />
            </div>
          </div>

          <div className="mt-4 flex justify-between items-center text-sm">
            <span className="text-gray-600">
              {filteredReservations.length} résultat(s) trouvé(s)
            </span>
            {selectedRows.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-medium">
                  {selectedRows.length} sélectionnée(s)
                </span>
                <button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                >
                  Actions groupées
                </button>
              </div>
            )}
          </div>

          {showBulkActions && selectedRows.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Actions groupées :</p>
              <div className="flex gap-2 flex-wrap">
                {steps.map((status) => (
                  <button
                    key={status}
                    onClick={() => handleBulkStatusChange(status)}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
                  >
                    → {STATUS_LABELS[status]}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

      {/* Tableau moderne */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-blue-600 to-indigo-600">
              <tr>
                <th className="px-4 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === paginatedReservations.length && paginatedReservations.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">Référence / PNR</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">Client</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">Montant Client</th>
                {hasPermission('view_profits') && (
                  <th className="px-4 py-4 text-left text-sm font-semibold text-white">Bénéfice</th>
                )}
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">Paiement</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">Statut</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">Date</th>
                <th className="px-4 py-4 text-center text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
            {paginatedReservations.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <div className="flex flex-col items-center justify-center text-gray-400">
                    <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-lg font-medium">Aucune réservation trouvée</p>
                    <p className="text-sm mt-1">Essayez d'ajuster vos filtres de recherche</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedReservations.map((r) => (
                <tr
                  key={r.id}
                  className={`hover:bg-blue-50 transition-colors ${
                    selectedRows.includes(r.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(r.id)}
                      onChange={() => {
                        setSelectedRows(prev =>
                          prev.includes(r.id)
                            ? prev.filter(id => id !== r.id)
                            : [...prev, r.id]
                        );
                      }}
                      className="w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col gap-1">
                      <span className="font-mono text-sm font-semibold text-gray-800">
                        {r.reference}
                      </span>
                      {r.pnr_code ? (
                        <span className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded inline-block w-fit">
                          PNR: {r.pnr_code}
                        </span>
                      ) : (
                        <button
                          onClick={() => {
                            setShowPNRModal({ id: r.id, currentPNR: '' });
                            setPnrInput('');
                          }}
                          className="text-xs text-gray-500 hover:text-blue-600 underline text-left"
                        >
                          + Ajouter PNR
                        </button>
                      )}
                      <button
                        onClick={() => {
                          try {
                            const passengers = typeof r.passenger_data === 'string' 
                              ? JSON.parse(r.passenger_data) 
                              : r.passenger_data;
                            const currentTickets = r.ticket_numbers 
                              ? (typeof r.ticket_numbers === 'string' ? JSON.parse(r.ticket_numbers) : r.ticket_numbers)
                              : [];
                            setShowTicketsModal({ id: r.id, passengers, currentTickets });
                            setTicketInputs(passengers.map((p, idx) => ({
                              passenger_index: idx,
                              passenger_name: `${p.name?.firstName || ''} ${p.name?.lastName || ''}`,
                              ticket_number: currentTickets.find(t => t.passenger_index === idx)?.ticket_number || ''
                            })));
                          } catch (e) {
                            console.error('Erreur parsing:', e);
                            toast.error('Erreur de chargement des données passagers');
                          }
                        }}
                        className="text-xs text-green-600 hover:text-green-700 underline text-left font-medium"
                      >
                        🎫 Billets
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-800 text-sm">
                        {r.customer_email}
                      </span>
                      <span className="text-xs text-gray-500 mt-0.5">
                        📞 {r.customer_phone}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="font-bold text-gray-800">
                      {parseFloat(r.payment_amount).toLocaleString()} XOF
                    </span>
                  </td>
                  {hasPermission('view_profits') && (
                    <td className="px-4 py-4">
                      {r.payment_status === "success" ? (
                        <div className="flex flex-col">
                          <span className={`font-bold ${calculateProfit(r) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {calculateProfit(r).toLocaleString()} XOF
                          </span>
                          <span className="text-xs text-gray-500">
                            {((calculateProfit(r) / parseFloat(r.payment_amount)) * 100).toFixed(1)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">En attente</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-4">
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        PAYMENT_STATUS_COLORS[r.payment_status] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {r.payment_status === "success" ? "✓ Payé" : 
                       r.payment_status === "pending" ? "⏳ En attente" : "✗ Échoué"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <select
                      value={r.reservation_status}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg border-2 focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                        STATUS_COLORS[r.reservation_status]
                      }`}
                    >
                      {steps.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {new Date(r.created_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => openDetails(r)}
                        className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors"
                        title="Voir détails"
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => openTracking(r)}
                        className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg shadow-sm transition-colors"
                        title="Suivre"
                      >
                        🧭
                      </button>
                      {hasPermission('mark_paid') && (
                        <button
                          onClick={() => setConfirmingPayment(r)}
                          className={`p-2 rounded-lg shadow-sm transition-colors ${
                            r.payment_status === "success"
                              ? "bg-green-200 text-green-800 cursor-not-allowed"
                              : "bg-green-500 hover:bg-green-600 text-white"
                          }`}
                          disabled={r.payment_status === "success"}
                          title="Marquer comme payé"
                        >
                          💳
                        </button>
                      )}
                      {r.reservation_status === "en_attente" && hasPermission('confirm_amadeus') && (
                        <button
                          onClick={() => confirmWithAmadeus(r)}
                          className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm transition-colors"
                          title="Confirmer avec Amadeus"
                        >
                          ✅
                        </button>
                      )}
                      {r.reservation_status === "confirmée" && hasPermission('print') && (
                        <>
                          <button
                            onClick={() => printReservation(r)}
                            className="p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-sm transition-colors"
                            title="Imprimer"
                          >
                            🖨️
                          </button>
                          <button
                            onClick={() => sendConfirmationEmail(r.id)}
                            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors"
                            title="Envoyer confirmation par email"
                          >
                            📧
                          </button>
                        </>
                      )}
                      {admin?.role === 'admin' && (
                        <button
                          onClick={() => setShowDeleteModal({ id: r.id, reference: r.reference })}
                          className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-sm transition-colors"
                          title="Supprimer la réservation"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <Pagination />

      {/* Modals */}
      {selected && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-white w-full max-w-3xl p-8 rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={closeDetails}
              className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full shadow hover:bg-red-600"
              title="Fermer"
            >
              ✖
            </button>
            <h2 className="text-2xl font-bold mb-4 text-blue-700">
              ✈️ Détails Réservation{" "}
              <span className="font-mono">{selected.reference}</span>
            </h2>
            
            {/* Informations PNR et Comptabilité */}
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className={`grid grid-cols-1 ${hasPermission('view_profits') ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Code PNR</p>
                  <div className="flex items-center gap-2">
                    {selected.pnr_code ? (
                      <>
                        <span className="font-mono font-bold text-blue-700">{selected.pnr_code}</span>
                        {hasPermission('manage_pnr') && (
                          <button
                            onClick={() => {
                              setShowPNRModal({ id: selected.id, currentPNR: selected.pnr_code });
                              setPnrInput(selected.pnr_code);
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Modifier
                          </button>
                        )}
                      </>
                    ) : (
                      hasPermission('manage_pnr') && (
                        <button
                          onClick={() => {
                            setShowPNRModal({ id: selected.id, currentPNR: '' });
                            setPnrInput('');
                          }}
                          className="text-sm text-blue-600 hover:underline font-medium"
                        >
                          + Ajouter PNR
                        </button>
                      )
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Montant Client</p>
                  <p className="font-bold text-green-700">{parseFloat(selected.payment_amount).toLocaleString()} XOF</p>
                </div>
                {hasPermission('view_profits') && (
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Bénéfice Net</p>
                    <p className={`font-bold ${calculateProfit(selected) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {calculateProfit(selected).toLocaleString()} XOF
                      <span className="text-xs ml-2">
                        ({((calculateProfit(selected) / parseFloat(selected.payment_amount)) * 100).toFixed(1)}%)
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2 text-lg">👤 Passager(s)</h3>
                {selected.passenger_data?.map((p, idx) => {
                  // Récupérer le numéro de billet pour ce passager
                  let ticketNumber = null;
                  try {
                    const tickets = selected.ticket_numbers 
                      ? (typeof selected.ticket_numbers === 'string' 
                          ? JSON.parse(selected.ticket_numbers) 
                          : selected.ticket_numbers)
                      : [];
                    ticketNumber = tickets.find(t => t.passenger_index === idx)?.ticket_number;
                  } catch (e) {
                    console.error('Erreur parsing ticket_numbers:', e);
                  }

                  return (
                    <div key={idx} className="border p-3 mb-2 rounded bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-lg font-bold">
                          {p.name?.firstName} {p.name?.lastName}{" "}
                          <span className="text-sm text-gray-600">
                            ({p.travelerType} - {p.gender})
                          </span>
                        </p>
                        {ticketNumber && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-mono">
                            🎫 {ticketNumber}
                          </span>
                        )}
                      </div>

                      {/* Civilité si enregistrée */}
                      {p.civility && <p>🧑 Civilité : {p.civility}</p>}

                      <p>📅 Naissance : {p.dateOfBirth}</p>

                      {/* Coordonnées si dispo */}
                      {p.contact?.emailAddress && <p>📧 Email : {p.contact.emailAddress}</p>}
                      {p.contact?.phones?.[0] && (
                        <p>
                          📞 Téléphone : +{p.contact.phones[0].countryCallingCode}{" "}
                          {p.contact.phones[0].number}
                        </p>
                      )}

                      {/* Document */}
                      {p.documents?.[0] && (
                        <div className="mt-2 border-t pt-2">
                          <p className="font-semibold">🪪 Document</p>
                          <p>Type : {p.documents[0].documentType}</p>
                          <p>Numéro : {p.documents[0].number}</p>
                          <p>Expiration : {p.documents[0].expiryDate}</p>
                          <p>Pays émetteur : {p.documents[0].issuanceCountry}</p>
                          <p>Nationalité : {p.documents[0].nationality}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div>
                <h3 className="font-semibold mb-2 text-lg">🛫 Détails du vol</h3>
                {selected.flight_data?.map((f, idx) => (
                  <div key={idx} className="border p-3 mb-3 rounded bg-gray-50">

                    {/* --- Itinéraires --- */}
                    {f.itineraries.map((it, i) => (
                      <div key={i} className="mb-3">
                        <h4 className="font-semibold text-gray-700 mb-2">
                          Itinéraire {i + 1} • Durée totale : {it.duration}
                        </h4>
                        {it.segments.map((s, j) => (
                          <div key={j} className="mb-3 pl-2 border-l-4 border-blue-400">
                            <p>
                              <span className="font-bold">{s.departure.iataCode}</span>{" "}
                              ({new Date(s.departure.at).toLocaleString()}) ➝{" "}
                              <span className="font-bold">{s.arrival.iataCode}</span>{" "}
                              ({new Date(s.arrival.at).toLocaleString()})
                            </p>
                            <p>
                              ✈️ {s.carrierCode} {s.number} | Avion : {s.aircraft.code}
                            </p>
                            <p>
                              👤 Opéré par :{" "}
                              {s.operating?.carrierCode
                                ? s.operating.carrierCode
                                : s.carrierCode}
                            </p>
                            <p>⏱️ Durée : {s.duration}</p>
                            <p>
                              🛋️ Classe cabine :{" "}
                              {s.class ? s.class : "N/A"} •
                              Tarif : {s.fareDetailsBySegment?.[0]?.cabin || "N/A"}
                            </p>
                            <p>
                              🎒 Bagages inclus :{" "}
                              {s.fareDetailsBySegment?.[0]?.includedCheckedBags?.quantity
                                ? `${s.fareDetailsBySegment[0].includedCheckedBags.quantity} bagage(s)`
                                : "Non inclus"}
                            </p>
                            {j < it.segments.length - 1 && (
                              <p className="text-sm text-gray-500 italic">
                                ⏳ Escale à {it.segments[j + 1].departure.iataCode} avant le prochain vol
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}

                    {/* --- Informations tarifaires --- */}
                    <div className="mt-2">
                      <p className="font-bold text-blue-700">
                        💰 Prix total : {f.price.total} {f.price.currency}
                      </p>
                      <p>💵 Tarif de base : {f.price.base} {f.price.currency}</p>
                      <p>🧾 Taxes : {f.price.fees?.reduce((sum, fee) => sum + parseFloat(fee.amount), 0)} {f.price.currency}</p>
                      {f.pricingOptions && (
                        <p>
                          🔄 Rebooking possible :{" "}
                          {f.pricingOptions.refundable ? "Oui (remboursable)" : "Non"} |{" "}
                          {f.pricingOptions.changeable ? "Modifiable" : "Non modifiable"}
                        </p>
                      )}
                    </div>

                    {/* --- Rebooking / Conditions tarifaires --- */}
                    {f.validatingAirlineCodes && (
                      <p className="mt-1 text-sm text-gray-600">
                        ✈️ Compagnie validatrice : {f.validatingAirlineCodes.join(", ")}
                      </p>
                    )}
                    {f.travelerPricings?.map((tp, tIdx) => (
                      <div key={tIdx} className="mt-2 text-sm text-gray-700">
                        👤 Voyageur {tIdx + 1} ({tp.travelerType}) — Tarif :{" "}
                        {tp.fareOption}
                        {tp.fareDetailsBySegment?.map((fd, fdIdx) => (
                          <div key={fdIdx}>
                            🛋️ Cabine : {fd.cabin}, Classe : {fd.class} (
                            {fd.includedCheckedBags?.quantity || 0} bagage(s) inclus)
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      )}

      {tracking && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-white w-full max-w-xl p-8 rounded-xl shadow-2xl relative">
            <button
              onClick={closeTracking}
              className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full shadow hover:bg-red-600"
              title="Fermer"
            >
              ✖
            </button>
            <h2 className="text-2xl font-bold mb-6 text-purple-700">
              🔍 Suivi Réservation{" "}
              <span className="font-mono">{tracking.reference}</span>
            </h2>
            <div className="flex justify-between items-center mb-8">
              {steps.map((step, i) => {
                const activeIndex = steps.indexOf(tracking.reservation_status);
                const isActive = i <= activeIndex;
                return (
                  <div key={step} className="flex-1 text-center relative">
                    <div
                      className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-bold text-lg ${isActive
                        ? "bg-green-500 text-white"
                        : "bg-gray-300 text-gray-500"
                        }`}
                    >
                      {i + 1}
                    </div>
                    <p
                      className={`mt-2 text-sm ${isActive
                        ? "text-green-700 font-bold"
                        : "text-gray-500"
                        }`}
                    >
                      {STATUS_LABELS[step]}
                    </p>
                    {i < steps.length - 1 && (
                      <div
                        className={`absolute top-5 left-1/2 w-full h-1 ${isActive ? "bg-green-500" : "bg-gray-300"
                          }`}
                        style={{ transform: "translateX(50%)" }}
                      ></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}










      {/* Modal PNR */}
      {showPNRModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-2xl relative">
            <button
              onClick={() => {
                setShowPNRModal(null);
                setPnrInput('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✖
            </button>
            <h2 className="text-xl font-bold mb-4 text-blue-700">✈️ Ajouter/Modifier Code PNR</h2>
            <p className="text-sm text-gray-600 mb-4">
              Entrez le code PNR (Passenger Name Record) de la compagnie aérienne
            </p>
            <input
              type="text"
              placeholder="Ex: ABC123"
              value={pnrInput}
              onChange={(e) => setPnrInput(e.target.value.toUpperCase())}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-lg"
              maxLength={10}
              autoFocus
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  if (pnrInput.trim()) {
                    updatePNR(showPNRModal.id, pnrInput.trim());
                    setShowPNRModal(null);
                    setPnrInput('');
                  } else {
                    toast.error("Veuillez entrer un code PNR");
                  }
                }}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg shadow font-medium"
              >
                ✓ Enregistrer
              </button>
              <button
                onClick={() => {
                  setShowPNRModal(null);
                  setPnrInput('');
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-medium"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Numéros de Billets */}
      {showTicketsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-white w-full max-w-2xl p-6 rounded-xl shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowTicketsModal(null);
                setTicketInputs([]);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✖
            </button>
            <h2 className="text-xl font-bold mb-4 text-green-700">🎫 Gérer les Numéros de Billets</h2>
            <p className="text-sm text-gray-600 mb-6">
              Entrez le numéro de billet pour chaque passager (13 chiffres généralement)
            </p>
            
            <div className="space-y-4">
              {ticketInputs.map((input, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">
                        {idx + 1}. {input.passenger_name}
                      </p>
                      <p className="text-xs text-gray-500">Passager {idx + 1}</p>
                    </div>
                    {input.ticket_number && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        ✓ Billet enregistré
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Ex: 0012345678901"
                    value={input.ticket_number}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTicketInputs(prev => 
                        prev.map((item, i) => 
                          i === idx ? { ...item, ticket_number: value } : item
                        )
                      );
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-base"
                    maxLength={13}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {input.ticket_number.length}/13 caractères
                  </p>
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  const ticketsToSave = ticketInputs
                    .filter(t => t.ticket_number.trim())
                    .map(t => ({
                      passenger_index: t.passenger_index,
                      passenger_name: t.passenger_name,
                      ticket_number: t.ticket_number
                    }));
                  
                  if (ticketsToSave.length === 0) {
                    toast.error("Veuillez entrer au moins un numéro de billet");
                    return;
                  }
                  
                  updateTicketNumbers(showTicketsModal.id, ticketsToSave);
                  setShowTicketsModal(null);
                  setTicketInputs([]);
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg shadow font-medium"
              >
                ✓ Enregistrer tous les billets
              </button>
              <button
                onClick={() => {
                  setShowTicketsModal(null);
                  setTicketInputs([]);
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-medium"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de paiement */}
      {confirmingPayment && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-2xl relative text-center">
            <h2 className="text-xl font-bold mb-4 text-blue-700">⚠️ Confirmation</h2>
            <p className="mb-6 text-gray-700">
              Voulez-vous vraiment marquer la réservation{" "}
              <span className="font-mono font-bold">{confirmingPayment.reference}</span>{" "}
              comme <span className="text-green-600 font-bold">payée</span> ?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  markAsPaid(confirmingPayment.id);
                  setConfirmingPayment(null);
                }}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg shadow"
              >
                ✅ Oui, confirmer
              </button>
              <button
                onClick={() => setConfirmingPayment(null)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg shadow"
              >
                ❌ Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'export */}
      {showExportModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-2xl relative">
            <button
              onClick={() => setShowExportModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✖
            </button>
            <h2 className="text-xl font-bold mb-4 text-gray-800">📤 Exporter les données</h2>
            <p className="text-sm text-gray-600 mb-6">
              Exporter {filteredReservations.length} réservation(s) filtrée(s)
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  exportToCSV();
                  setShowExportModal(false);
                }}
                className="w-full p-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition font-medium"
              >
                📊 Exporter en CSV
              </button>
              <button
                onClick={() => {
                  exportToPDF();
                  setShowExportModal(false);
                }}
                className="w-full p-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition font-medium"
              >
                📄 Exporter en PDF
              </button>
              <button
                onClick={() => setShowExportModal(false)}
                className="w-full p-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition font-medium"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl relative overflow-hidden animate-scale-in">
            {/* Header avec dégradé rouge */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-3xl">⚠️</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Confirmation de suppression</h2>
                    <p className="text-red-100 text-sm">Cette action est irréversible</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6">
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6">
                <p className="text-gray-800 mb-3">
                  Êtes-vous sûr de vouloir supprimer définitivement la réservation :
                </p>
                <div className="bg-white p-3 rounded-lg border border-red-200">
                  <p className="font-mono font-bold text-lg text-red-700">
                    {showDeleteModal.reference}
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800 mb-2">Attention !</p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• Toutes les données seront perdues</li>
                      <li>• Les informations passagers seront supprimées</li>
                      <li>• Cette action ne peut pas être annulée</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium transition flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Annuler
                </button>
                <button
                  onClick={() => deleteReservation(showDeleteModal.id)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg font-bold transition shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default AdminReservationsApp;
